import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useForgotPassword } from "@/features/auth/api/useAuth";
import { emailOnlySchema, type EmailOnlyValues } from "@/features/auth/lib/schemas";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { Field } from "@/shared/ui/Field";

export function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const forgot = useForgotPassword();
  const errorMessage = useApiErrorMessage();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailOnlyValues>({ resolver: zodResolver(emailOnlySchema) });

  const onSubmit = handleSubmit((values) => {
    forgot.mutate(values.email);
  });

  if (forgot.isSuccess) {
    return (
      <Card className="mx-auto max-w-md">
        <h1 className="mb-4 text-2xl font-bold tracking-tight">{t("forgotPassword.heading")}</h1>
        {/* Message volontairement identique que le compte existe ou non : le
            contraire publierait la liste des adresses inscrites (docs/06 §5). */}
        <Alert tone="success">{t("forgotPassword.sent")}</Alert>
      </Card>
    );
  }

  return (
    <Card className="mx-auto max-w-md">
      <h1 className="mb-2 text-2xl font-bold tracking-tight">{t("forgotPassword.heading")}</h1>
      <p className="mb-6 text-sm text-slate-600 dark:text-slate-400">{t("forgotPassword.intro")}</p>

      {forgot.isError && (
        <Alert tone="error" className="mb-4">
          {errorMessage(forgot.error)}
        </Alert>
      )}

      <form
        onSubmit={(event) => {
          // `handleSubmit` rend toujours une fonction asynchrone, même sur un
          // gestionnaire synchrone : `void` dit explicitement qu'on n'attend
          // pas la promesse, ce que React ne saurait pas faire de toute façon.
          void onSubmit(event);
        }}
        className="space-y-4"
        noValidate
      >
        <Field
          label={t("fields.email")}
          type="email"
          autoComplete="email"
          error={errors.email && t("validation.email")}
          {...register("email")}
        />
        <Button type="submit" className="w-full" disabled={forgot.isPending}>
          {forgot.isPending ? t("forgotPassword.submitting") : t("forgotPassword.submit")}
        </Button>
      </form>

      <p className="mt-6 text-sm">
        <Link to="/login" className="text-brand-600 hover:underline">
          {t("forgotPassword.backToLogin")}
        </Link>
      </p>
    </Card>
  );
}
